// NEXT UP — the shell's calendar widget (HOME_AMBIENT_SKIN_SPEC.md §4;
// DESIGN-WL-HOME-WIDGETS-1). Size M by default, never smaller (its hero, the
// event line and the day list need two cells); L gives the day list the
// height for five rows.
//
//   hero      "in 42 min" · "now" (under way) · "in 3 h" · the weekday
//   delta     the next event: its title (Z 500, wraps — never cut), the mono
//             "14:30 – 15:00 · Tbilisi", and ONE capsule: Join when it has a
//             meeting room or a call link, else Open (its detail sheet)
//   visual    the events after it — a hue mark, the title, the mono start
//             (the weekday when it is not today); two at M, five at L
//   empty     "Nothing coming up" + New booking, once the calendar window
//             has answered (never while it is unread); a failed read says
//             so ("Couldn't load the calendar.") — never a false "nothing"
//             and never a skeleton that waits forever
//
// Data: the shell's own calendar window (`root.intranetEvents`,
// `root.intranetEventsLoaded`) AND the installed apps' own upcoming items —
// the CRM's viewings first (Nika, 2026-09-28) — through the `homeUpcoming`
// provider contract (functions/homeNextUp.js header). One merged list,
// soonest first: the hero and the event line are the next ITEM (an event or
// a viewing), the rows the ones after it; an app item's Open takes its route
// through openInSlot. The provider pass lives on this body's own state
// (`upcoming`), so "nothing" waits until the calendar AND the providers
// answered. The body keeps its own `now` and a minute clock
// (homeNextUpClock), so the countdown moves, a finished item leaves and the
// providers refresh without a reload.
export const HomeNextUpBody = {
  extends: 'HomeWidgetBody',
  state: { now: 0, upcoming: null },
  // The installed apps publish after the first paint on a cold load: the body
  // re-renders when they do, and the pass re-runs for a new provider.
  subscribeTo: ['workspaceApps', 'workspaceProject'],
  onRender: (el) => {
    el.call('homeNextUpClock')
    el.call('homeNextUpCollect')
  },
  onUpdate: (el) => el.call('homeNextUpCollect'),
  onRemove: (el) => el.call('homeNextUpClockStop'),
  // "Nothing" only once the calendar (loaded or failed), the installed apps
  // (known) AND every provider of them answered — never while a viewing may
  // still be on its way.
  empty: (el, s) => {
    void s.root.intranetEvents
    void s.root.workspaceApps
    void s.root.workspaceProject
    void s.upcoming
    if (!s.root.intranetEventsLoaded && !s.root.intranetEventsError) return false
    if (!el.call('homeNextUpAppsKnown', s.root)) return false
    const sig = el.call('homeNextUpProviderSig', s.root)
    if (sig && !(s.upcoming && s.upcoming.answered && s.upcoming.sig === sig)) return false
    return !el.call('homeNextUpItems', s.root, s.now, s.upcoming).length
  },

  HomeWidgetHero: {
    unitFirst: true,
    Value: {
      text: (el, s) => {
        void s.root.intranetEvents
        void s.upcoming
        const [next] = el.call('homeNextUpItems', s.root, s.now, s.upcoming)
        const hero = next ? el.call('homeNextUpHero', next, s.now) : null
        return hero ? hero.value : null
      }
    },
    Unit: {
      text: (el, s) => {
        void s.root.intranetEvents
        void s.upcoming
        const [next] = el.call('homeNextUpItems', s.root, s.now, s.upcoming)
        const hero = next ? el.call('homeNextUpHero', next, s.now) : null
        return hero ? hero.before : ''
      },
      hide: (el) => !el.call('homeSlotText', el) || el.call('homeSlotText', el.parent.Value) == null
    },
    Tail: {
      text: (el, s) => {
        void s.root.intranetEvents
        void s.upcoming
        const [next] = el.call('homeNextUpItems', s.root, s.now, s.upcoming)
        const hero = next ? el.call('homeNextUpHero', next, s.now) : null
        return hero ? hero.after : ''
      }
    }
  },

  NextUpEvent: {
    gridArea: 'delta',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 'X2',
    minWidth: '0',
    hide: (el, s) => {
      void s.root.intranetEvents
      void s.upcoming
      return !el.call('homeNextUpItems', s.root, s.now, s.upcoming).length
    },

    NextUpTitle: {
      tag: 'span',
      class: 'widget-row-title',
      fontSize: 'Z',
      fontWeight: '500',
      lineHeight: '1.35',
      color: 'title',
      maxWidth: '100%',
      overflowWrap: 'anywhere',
      text: (el, s) => {
        void s.root.intranetEvents
        void s.upcoming
        const [next] = el.call('homeNextUpItems', s.root, s.now, s.upcoming)
        return next ? next.title || '' : ''
      }
    },

    NextUpTime: {
      tag: 'span',
      fontFamily: 'Code',
      fontSize: 'Y',
      fontVariantNumeric: 'tabular-nums',
      color: 'inactive',
      maxWidth: '100%',
      overflowWrap: 'anywhere',
      text: (el, s) => {
        void s.root.intranetEvents
        void s.upcoming
        const [next] = el.call('homeNextUpItems', s.root, s.now, s.upcoming)
        return next ? el.call('homeNextUpTime', next) : ''
      }
    },

    NextUpGo: {
      tag: 'button',
      type: 'button',
      marginTop: 'X',
      theme: 'layer',
      color: 'title',
      minHeight: 'controlHCompact',
      padding: '0 A',
      round: 'radiusPill',
      fontSize: 'Z2',
      fontWeight: '500',
      border: 'none',
      cursor: 'pointer',
      ':hover': { background: 'selected' },
      ':active': { background: 'selected' },
      text: (el, s) => {
        void s.root.intranetEvents
        void s.upcoming
        const [next] = el.call('homeNextUpItems', s.root, s.now, s.upcoming)
        const action = next ? el.call('homeNextUpAction', next) : null
        return action ? el.call('polyglot', action.label) : ''
      },
      onClick: (e, el, s) => {
        e.stopPropagation()
        const [next] = el.call('homeNextUpItems', s.root, s.now, s.upcoming)
        const action = next ? el.call('homeNextUpAction', next) : null
        if (action) el.call(action.call, el.call('homeNextUpActionArg', next))
      }
    }
  },

  HomeWidgetList: {
    asVisual: true,
    max: 5,
    items: (el, s) => {
      void s.root.intranetEvents
      void s.upcoming
      const up = s.upcoming && Array.isArray(s.upcoming.items) ? s.upcoming.items : []
      if (!s.root.intranetEventsLoaded && !(s.root.intranetEvents || []).length && !up.length) return null
      return el.call('homeNextUpRows', s.root, s.now, s.upcoming)
    }
  },

  // Two rows fit beside the event at M; L gives the list the height for five.
  ':is([data-widget-size="m"]) & .home-widget-list > :nth-child(n+3)': { display: 'none' },

  HomeWidgetEmpty: {
    Text: {
      text: (el, s) =>
        el.call(
          'polyglot',
          s.root.intranetEventsError ? 'workspace.widget.nextUp.error' : 'workspace.widget.nextUp.empty'
        )
    },
    Action: {
      hide: (el, s) => !!s.root.intranetEventsError,
      text: '{{ workspace.widget.nextUp.newBooking | polyglot }}',
      onClick: (e, el) => {
        e.stopPropagation()
        el.call('openNewBookingModal', { date: el.call('todayISO') })
      }
    }
  }
}
